if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
        
        // Listen for variant updates to refresh tickets display
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, async ({ data }) => {
          if (data && data.variant) {
            await this.updateTicketsDisplay(data.variant);
          }
        });
        
        // Cache for product data
        this._productDataCache = null;
      }

      async getFullProductData() {
        // Return cached data if available
        if (this._productDataCache) {
          console.log('[Tickets] Using cached product data');
          return this._productDataCache;
        }
        
        console.log('[Tickets] Fetching product data...');
        
        // FIRST: Try to read variants inventory from our custom script (most reliable)
        const variantsInventoryScript = this.querySelector('script[data-variants-inventory]');
        console.log('[Tickets] variants-inventory script found:', !!variantsInventoryScript);
        
        if (variantsInventoryScript && variantsInventoryScript.textContent) {
          try {
            const variantsMap = JSON.parse(variantsInventoryScript.textContent);
            console.log('[Tickets] Parsed variants inventory map:', Object.keys(variantsMap).length, 'variants');
            // Convert map to array format and also keep the map for fast lookups
            const variants = Object.values(variantsMap);
            this._productDataCache = { variants, variantsMap };
            return this._productDataCache;
          } catch (e) {
            console.error('[Tickets] Failed to parse variants inventory:', e);
          }
        }
        
        // Try to find product data from sticky-atc-bar JSON
        const stickyAtcJson = document.querySelector('sticky-atc-bar script[type="application/json"]');
        console.log('[Tickets] sticky-atc-bar JSON found:', !!stickyAtcJson);
        
        if (stickyAtcJson && stickyAtcJson.textContent) {
          try {
            const variants = JSON.parse(stickyAtcJson.textContent);
            console.log('[Tickets] Parsed variants from sticky-atc-bar:', variants.length, 'variants');
            if (Array.isArray(variants) && variants.length > 0) {
              this._productDataCache = { variants };
              return this._productDataCache;
            }
          } catch (e) {
            console.error('[Tickets] Failed to parse sticky-atc-bar JSON:', e);
          }
        }
        
        // Fallback: fetch from Shopify API (won't have inventory though)
        const productUrl = this.dataset.url;
        console.log('[Tickets] Fetching from API:', productUrl);
        if (productUrl) {
          try {
            const response = await fetch(`${productUrl}.js`);
            const productData = await response.json();
            console.log('[Tickets] Fetched product data from API, variants:', productData.variants?.length);
            this._productDataCache = productData;
            return productData;
          } catch (e) {
            console.error('[Tickets] Failed to fetch product data:', e);
          }
        }
        
        return null;
      }

      async updateTicketsDisplay(variantData) {
        const ticketsElement = this.querySelector('[data-tickets-left]');
        const ticketsCountSpan = ticketsElement?.querySelector('[data-tickets-count]');
        const ticketNumber = ticketsElement?.querySelector('[data-ticket-number]');
        
        if (!ticketsElement || !ticketsCountSpan) {
          console.log('[Tickets] Element not found');
          return;
        }
        
        console.log('[Tickets] Called with variant:', variantData);
        console.log('[Tickets] Has inventory_quantity?', variantData?.inventory_quantity);
        
        // If variantData doesn't have inventory, try to get it from full product data
        if (variantData && (variantData.inventory_quantity === undefined || variantData.inventory_quantity === null) && variantData.id) {
          console.log('[Tickets] Fetching full product data for variant ID:', variantData.id);
          const productData = await this.getFullProductData();
          console.log('[Tickets] Product data received:', !!productData);
          
          if (productData) {
            // Try map first (faster lookup by ID)
            if (productData.variantsMap && productData.variantsMap[variantData.id]) {
              console.log('[Tickets] Found variant in map');
              variantData = productData.variantsMap[variantData.id];
            } else if (productData.variants) {
              // Fallback to array search
              console.log('[Tickets] Searching through', productData.variants.length, 'variants');
              const fullVariant = productData.variants.find(v => v.id === variantData.id);
              console.log('[Tickets] Found matching variant:', !!fullVariant);
              if (fullVariant) {
                console.log('[Tickets] Full variant inventory_quantity:', fullVariant.inventory_quantity);
                variantData = fullVariant;
              }
            }
          }
        }
        
        // If still no data, try reading from JSON script as fallback
        if (!variantData || (variantData.inventory_quantity === undefined && variantData.inventory_quantity !== null)) {
          console.log('[Tickets] Trying to read from data-selected-variant script');
          const jsonScript = this.querySelector('script[data-selected-variant]');
          if (jsonScript && jsonScript.textContent) {
            try {
              variantData = JSON.parse(jsonScript.textContent);
              console.log('[Tickets] Parsed variant from script:', variantData);
            } catch (e) {
              console.error('[Tickets] Failed to parse variant data:', e);
              return;
            }
          }
        }
        
        if (!variantData) {
          console.log('[Tickets] No variant data available');
          return;
        }
        
        const quantity = variantData.inventory_quantity;
        const available = variantData.available;
        
        console.log('[Tickets] Final values - quantity:', quantity, 'available:', available);
        
        // Remove all state classes
        ticketsElement.classList.remove('tickets-left--unavailable', 'tickets-left--low');
        
        // Check availability first
        if (!available) {
          if (ticketNumber) ticketNumber.textContent = '✕';
          ticketsCountSpan.textContent = 'Sold out';
          ticketsElement.classList.add('tickets-left--unavailable');
          console.log('[Tickets] Displaying: Sold out');
        } else if (quantity !== undefined && quantity !== null && quantity > 0) {
          // Inventory is tracked and we have a specific count
          if (ticketNumber) ticketNumber.textContent = quantity;
          const plural = quantity !== 1 ? 's' : '';
          ticketsCountSpan.textContent = `ticket${plural} remaining`;
          if (quantity <= 5) {
            ticketsElement.classList.add('tickets-left--low');
          }
          console.log('[Tickets] Displaying:', quantity, 'tickets remaining');
        } else {
          // Variant is available but inventory not tracked or unlimited
          if (ticketNumber) ticketNumber.textContent = '✓';
          ticketsCountSpan.textContent = 'Available to book';
          console.log('[Tickets] Displaying: Available to book');
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
